import React, { useState } from 'react';
import { Card, Group, Text, ActionIcon, TextInput, Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';

interface CalculatorProps {
    onCalculate: (result: number) => void;
    onClose: () => void;
}

export const Calculator = ({ onCalculate, onClose }: CalculatorProps) => {
    const [expression, setExpression] = useState('');

    const evaluateExpression = () => {
        try {
            // Replace × with * and ÷ with / for proper evaluation
            const sanitizedExpression = expression
                .replace(/×/g, '*')
                .replace(/÷/g, '/');

            // Use Function constructor to safely evaluate the expression
            const result = Function(`'use strict'; return (${sanitizedExpression})`)();

            if (isNaN(result) || !isFinite(result)) {
                throw new Error('Invalid result');
            }

            // Round to 2 decimal places
            const roundedResult = parseFloat(result.toFixed(2));
            onCalculate(roundedResult);
        } catch (error) {
            notifications.show({
                title: 'Invalid Expression',
                message: 'Please enter a valid mathematical expression',
                color: 'red',
            });
        }
    };

    const addToExpression = (value: string) => {
        setExpression(prev => prev + value);
    };

    return (
        <Card withBorder shadow="md" padding="md" radius="md" style={{ marginBottom: 16 }}>
            <Group justify="space-between" mb="sm">
                <Text size="sm" fw={500}>Calculator</Text>
                <ActionIcon variant="subtle" onClick={onClose}>
                    <Text>×</Text>
                </ActionIcon>
            </Group>

            <TextInput
                value={expression}
                onChange={(e) => setExpression(e.target.value)}
                placeholder="e.g. 12.99 + 4.50 + 7.25"
                rightSection={
                    <Button
                        variant="light"
                        onClick={evaluateExpression}
                        size="xs"
                    >
                        Calculate
                    </Button>
                }
            />

            <Group justify="space-between" mt="sm">
                <Button variant="subtle" size="xs" onClick={() => addToExpression('+')}>+</Button>
                <Button variant="subtle" size="xs" onClick={() => addToExpression('-')}>-</Button>
                <Button variant="subtle" size="xs" onClick={() => addToExpression('×')}>×</Button>
                <Button variant="subtle" size="xs" onClick={() => addToExpression('÷')}>÷</Button>
                <Button variant="subtle" size="xs" onClick={() => setExpression('')}>Clear</Button>
            </Group>
        </Card>
    );
}; 